import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Modal, CloseButton, Form, Carousel } from 'react-bootstrap';
import BottomNav from '../BottomNav';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../AuthContext';
import { PlusCircle, Star, StarFill, PencilSquare, PauseBtn, PlayBtn, Trash3 } from 'react-bootstrap-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Codee from '../ui/Codee';
import { useLocation } from 'react-router-dom';

const LIST_DELETE_ANIMATION_MS = 240;

const PriceChart = ({ data, theme, selectedPeriod }) => {
  // Определяем временные границы для выбранного периода
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="h5">📭 Нет данных для отображения</div>
        <p>Попробуйте выбрать другой временной диапазон</p>
      </div>
    );
  }
  const now = new Date();
  let startDate;
  switch (selectedPeriod) {
    case '1m':
      startDate = new Date(now.setMonth(now.getMonth() - 1));
      break;
    case '3m':
      startDate = new Date(now.setMonth(now.getMonth() - 3));
      break;
    case '6m':
      startDate = new Date(now.setMonth(now.getMonth() - 6));
      break;
    default:
      startDate = new Date(data[0][0] * 1000); // Начало истории
  }
  const startTs = Math.floor(startDate.getTime() / 1000);
  const endTs = Math.floor(Date.now() / 1000);

  // Фильтруем данные по выбранному периоду
  const filteredData = data.filter(([ts]) => ts >= startTs && ts <= endTs);
  if (filteredData.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="h5">📭 Нет данных за выбранный период</div>
        <p>Попробуйте выбрать другой временной диапазон</p>
      </div>
    );
  }
  // Форматируем данные для графика
  const chartData = filteredData.map(([ts, price]) => ({
    date: new Date(ts * 1000).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
    }),
    price,
    timestamp: ts, // Добавляем timestamp для уникальности
  }));

  // Цвета для тем
  const textColor = theme === 'dark' ? '#fff' : '#000';
  const lineColor = theme === 'dark' ? '#8884d8' : '#413ea0';
  const gridColor = theme === 'dark' ? '#424242' : '#e0e0e0';
  const tooltipStyle = {
    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };


  return (
    <div style={{ height: '400px', width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="date"
            stroke={textColor}
            tick={{ fill: textColor }}
            tickLine={{ stroke: textColor }}
          />
          <YAxis
            stroke={textColor}
            tick={{ fill: textColor }}
            tickLine={{ stroke: textColor }}
            tickFormatter={(value) => `${value.toLocaleString('ru-RU')}₽`}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value) => [`${value.toLocaleString('ru-RU')} ₽`, 'Цена']}
            labelStyle={{ color: textColor }}
          />
          <Line
            type="monotone"
            dataKey="price"
            stroke={lineColor}
            strokeWidth={2}
            dot={{ fill: lineColor, strokeWidth: 2 }}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const getPriceChange = (diapaz) => {
  if (!diapaz || diapaz.length < 2) return null;

  const last = diapaz[diapaz.length - 1][1];
  const prev = diapaz[diapaz.length - 2][1];
  const diff = last - prev;
  const percent = ((diff / prev) * 100).toFixed(1);

  return {
    diff,
    percent,
    isIncrease: diff > 0,
    isDecrease: diff < 0,
    hasChange: diff !== 0
  };
};

function processPrices(priceHistory) {
  if (!priceHistory || priceHistory.length === 0) return [];

  // Сортируем историю по времени (timestamp)
  const sortedHistory = priceHistory.slice().sort((a, b) => a[0] - b[0]);
  const currentTs = Math.floor(Date.now() / 1000); // текущее время в секундах
  const timeRanges = [];

  for (let i = 0; i < sortedHistory.length; i++) {
    const startTs = sortedHistory[i][0];
    const endTs = i < sortedHistory.length - 1 ? sortedHistory[i + 1][0] : currentTs;
    const price = sortedHistory[i][1];
    timeRanges.push([startTs, endTs, price]);
  }

  return timeRanges;
}

/**
 * Удаляет завышенные цены, превышающие Q3 + threshold*IQR.
 * Если данных меньше 4, возвращает исходный массив.
 */
function removeHighOutliers(prices, threshold = 1.0) {
  if (prices.length < 4) return prices;

  // Сортируем цены для расчёта квартилей
  const sortedPrices = prices.slice().sort((a, b) => a - b);
  const q1 = sortedPrices[Math.floor(sortedPrices.length / 4)];
  const q3 = sortedPrices[Math.floor(3 * sortedPrices.length / 4)];
  const iqr = q3 - q1;
  const upperBound = q3 + threshold * iqr;

  return prices.filter(p => p <= upperBound);
}

/**
 * Рассчитывает взвешенную медиану с учетом длительности цен и удаления завышенных выбросов.
 * timeRanges — массив вида [[start, end, price], ...]
 */
function calculateWeightedMedian(timeRanges) {
  if (!timeRanges || timeRanges.length === 0) return null;

  // Общая продолжительность всех интервалов
  const totalDuration = timeRanges.reduce((sum, [start, end]) => sum + (end - start), 0);
  if (totalDuration === 0) return null;

  // Формируем массив пар [price, weight]
  const weightedPrices = timeRanges.map(([start, end, price]) => {
    const weight = (end - start) / totalDuration;
    return [price, weight];
  });

  // Извлекаем цены и фильтруем завышенные выбросы
  const prices = weightedPrices.map(([price]) => price);
  const filteredPrices = removeHighOutliers(prices, 1.0);

  // Оставляем только интервалы, цена которых входит в отфильтрованный список
  let filteredWeighted = weightedPrices.filter(([price]) => filteredPrices.includes(price));

  // Если после фильтрации ничего не осталось — используем исходный список
  if (filteredWeighted.length === 0) {
    filteredWeighted = weightedPrices;
  }

  // Сортируем по цене по возрастанию
  filteredWeighted.sort((a, b) => a[0] - b[0]);

  // Ищем медиану по накоплению весов
  let cumulative = 0.0;
  for (const [price, weight] of filteredWeighted) {
    cumulative += weight;
    if (cumulative >= 0.5) {
      return price;
    }
  }

  return filteredWeighted[filteredWeighted.length - 1][0];
}
const WaitingListPage = () => {
  const { theme } = useTheme();
  const {
    userInfo,
    isAuthenticated,
    isLoading,
    createTrackingItem,
    updateTrackingItem,
    getTrackingList,
    deleteTrackingItem,
    parseTrackingItem,
    trackingItems,
    updateTrackingItemPause,
    ensurePushToken
  } = useAuth();

  const [logOutput, setLogOutput] = useState('');
  const [trackingList, setTrackingList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [itemData, setItemData] = useState(null);
  const [trackPrice, setTrackPrice] = useState(false);
  const [trackSeller, setTrackSeller] = useState(false);
  const [trackAvailability, setTrackAvailability] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [priceWant, setPriceWant] = useState(null);
  const [chartItem, setChartItem] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItemData, setEditItemData] = useState(null);
  const [editPriceWant, setEditPriceWant] = useState(null);
  const [editTrackSeller, setEditTrackSeller] = useState(false);
  const [editTrackPrice, setEditTrackPrice] = useState(false);
  const [editTrackAvailability, setEditTrackAvailability] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  const [searchQuery, setSearchQuery] = useState('');


  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const articleParam = params.get('article');

  const itemRefs = useRef({});
  const deletingTrackingTimersRef = useRef(new Map());
  const [deletingTrackingIds, setDeletingTrackingIds] = useState([]);

  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';

  const headerStyle = {
    padding: '1.5rem',
    marginBottom: '1rem',
    borderRadius: '0.5rem',
    transition: 'all 0.3s ease'
  };
  const handleSizeToggle = (size) => {
    // Проверяем, что данные о товаре уже загружены
    if (!itemData) return;

    setItemData(prevItemData => {
      const newSizes = { ...prevItemData.data.sizes };
      const [wbStock, sellerStock, price, isSelected] = newSizes[size];
      newSizes[size] = [wbStock, sellerStock, price, !isSelected];

      return {
        ...prevItemData,
        data: {
          ...prevItemData.data,
          sizes: newSizes
        }
      };
    });
  };

  const handleEditSizeToggle = (size) => {
    if (!editItemData) return;
    setEditItemData(prev => {
      const newSizes = { ...prev.sizes };
      const [wbStock, sellerStock, price, isSelected] = newSizes[size];
      newSizes[size] = [wbStock, sellerStock, price, !isSelected];
      return { ...prev, sizes: newSizes };
    });
  };
  const handleEditItem = (item) => {
    // «Клонируем» объект, чтобы не мутировать исходные данные
    setEditItemData({ ...item });
    setEditPriceWant(item.price_want);
    setEditTrackSeller(item.prod);
    setEditTrackPrice(item.price_change_push);
    setEditTrackAvailability(item.stock_change_push);
    setErrorMessage('');
    setShowEditModal(true);
  };

  const ensureTrackingPushToken = async () => {
    const fcmToken = await ensurePushToken({ showInstructions: true });
    if (!fcmToken) {
      setErrorMessage('Чтобы получать уведомления по отслеживанию, разрешите push-уведомления на этом устройстве.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleTrackPriceChange = async (checked) => {
    if (checked && !(await ensureTrackingPushToken())) return;
    setTrackPrice(checked);
  };

  const handleTrackAvailabilityChange = async (checked) => {
    if (checked && !(await ensureTrackingPushToken())) return;
    setTrackAvailability(checked);
  };

  const handleEditTrackPriceChange = async (checked) => {
    if (checked && !(await ensureTrackingPushToken())) return;
    setEditTrackPrice(checked);
  };

  const handleEditTrackAvailabilityChange = async (checked) => {
    if (checked && !(await ensureTrackingPushToken())) return;
    setEditTrackAvailability(checked);
  };

  const handleEditSave = async () => {
    if (!editPriceWant || isNaN(editPriceWant)) {
      setErrorMessage('Укажите корректную желаемую цену');
      return;
    }
    if (!hasAtLeastOneSelectedSize(editItemData.sizes)) {
      setErrorMessage('Выберите хотя бы один размер для отслеживания');
      return;
    }
    if ((editTrackPrice || editTrackAvailability) && !(await ensureTrackingPushToken())) {
      return;
    }
    const updateData = {
      id: editItemData.id, // обязательно передаём идентификатор
      name: editItemData.name,
      brand: editItemData.brand,
      article: editItemData.article,
      raiting: editItemData.raiting,
      diapaz: editItemData.diapaz,
      feedbacks: editItemData.feedbacks,
      sizes: editItemData.sizes,
      price_now: editItemData.price_now,
      price_want: parseInt(editPriceWant, 10),
      linkphoto: editItemData.linkphoto,
      prod: editTrackSeller,
      price_change_push: editTrackPrice,
      stock_change_push: editTrackAvailability,
      pause: editItemData.pause
      // Обратите внимание, что поле диапазона (diapaz) мы исключаем
    };

    try {
      await updateTrackingItem(updateData);
      setShowEditModal(false);
      // Обновляем список после сохранения изменений
      getTrackingList();
    } catch (error) {
      setErrorMessage('Ошибка при сохранении изменений: ' + (error.message || 'Попробуйте снова.'));
    }
  };

  const handlePriceWantChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) { // Разрешаем только цифры
      setPriceWant(value);
    }
  };
  const handleAddItem = async () => {
    // Блокирующие проверки:
    if (!/^[0-9]+$/.test(inputValue)) {
      setErrorMessage('Артикул должен содержать только цифры.');
      return;
    }
    if (inputValue.length < 5) {
      setErrorMessage('Артикул должен содержать минимум 5 цифр.');
      return;
    }

    try {
      const item = await parseTrackingItem(inputValue);
      setItemData(item);
      setShowModal(false);
      setShowSuccessModal(true);
      setInputValue('');
      // Можно сбросить предупреждение, если хотите:
      // setErrorMessage('');
    } catch (error) {
      setErrorMessage('Ошибка при добавлении артикула: ' + (error.message || 'Попробуйте снова.'));
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    // Разрешаем только цифры
    if (!/^[0-9]*$/.test(value)) {
      setErrorMessage('Артикул должен содержать только цифры.');
    } else if (value.length > 0 && value.length < 5) {
      setErrorMessage('Артикул должен содержать минимум 5 цифр.');
    } else if (value !== '' && trackingItems.some(item => item.article === parseInt(value, 10))) {
      setErrorMessage('Такой артикул уже есть в списке. Вы добавляете дубликат.');
    } else {
      setErrorMessage('');
    }

    setInputValue(value);
  };



  const RatingStars = ({ rating, feedbacks }) => {
    const stars = [1, 2, 3, 4, 5].map((star) =>
      rating < star
        ? <Star key={star} size={14} color="orange" style={{ marginTop: 1 }} />
        : <StarFill key={star} size={14} color="orange" style={{ marginTop: 1 }} />
    );

    return (
      <div
        style={{
          display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
          backgroundColor: '#fff', borderRadius: 10, height: 21,
          alignItems: 'center', position: 'absolute', top: -10, right: 0
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '3px' }}>
          {stars}
        </div>
        <span style={{ color: 'orange', marginRight: 5, fontSize: 12, fontWeight: '400' }}> | {feedbacks}</span>
      </div>
    );
  };
  const hasAtLeastOneSelectedSize = (sizes) => {
    return Object.values(sizes).some(([, , , isSelected]) => isSelected);
  };



  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      handleInputChange({ target: { value: text } });
    } catch (error) {
      setErrorMessage('Не удалось вставить из буфера обмена.');
    }
  };

  // if (userInfo.user_id !== 7017255909) {
  //   return <div className={themeClass}>Скоро будет, уже работаем на полную</div>;
  // }

  // Загружаем список отслеживаемых товаров при монтировании компонента
  useEffect(() => {
    const fetchTrackingList = async () => {
      if (isAuthenticated && !isLoading) {
        getTrackingList();
      }
    };

    fetchTrackingList();
  }, []);

  useEffect(() => {
    if (articleParam && trackingItems.length > 0) {
      const targetItem = trackingItems.find(item => item.article.toString() === articleParam);
      if (targetItem && itemRefs.current[targetItem.article]) {
        setTimeout(() => {
          itemRefs.current[targetItem.article].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 300); // Небольшая задержка для стабилизации DOM
      }
    }
  }, [articleParam, trackingItems]);

  const handleSave = async () => {
    if (!priceWant || isNaN(priceWant)) {
      setErrorMessage('Укажите корректную желаемую цену');
      return;
    }

    if ((trackPrice || trackAvailability) && !(await ensureTrackingPushToken())) {
      return;
    }

    try {
      const trackingData = {
        diapaz: itemData.data.price_history || null,
        name: itemData.data.name,
        brand: itemData.data.brand,
        article: itemData.data.article,
        raiting: itemData.data.raiting,
        feedbacks: itemData.data.feedbacks,
        sizes: itemData.data.sizes,
        price_now: itemData.data.price_now,
        price_want: parseInt(priceWant, 10),
        linkphoto: itemData.data.links_photo,
        prod: trackSeller,
        price_change_push: trackPrice,
        stock_change_push: trackAvailability,
        pause: false
      };

      await createTrackingItem(trackingData);
      setShowSuccessModal(false);
      getTrackingList(); // Обновляем список отслеживаемых
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      setErrorMessage('Ошибка при сохранении. Попробуйте снова.');
    }
  };
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setTrackPrice(false);
    setTrackSeller(false);
    setTrackAvailability(false);
    setPriceWant(null);
  };

  const filteredItems = trackingItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    return () => {
      deletingTrackingTimersRef.current.forEach((timer) => clearTimeout(timer));
      deletingTrackingTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setDeletingTrackingIds((prev) => prev.filter((id) => trackingItems.some((item) => Number(item?.id || 0) === id)));
  }, [trackingItems]);

  const handleAnimatedTrackingDelete = (itemId) => {
    const normalizedId = Number(itemId || 0);
    if (normalizedId <= 0) return;
    if (deletingTrackingTimersRef.current.has(normalizedId)) return;

    setDeletingTrackingIds((prev) => (prev.includes(normalizedId) ? prev : [...prev, normalizedId]));

    const timer = setTimeout(async () => {
      deletingTrackingTimersRef.current.delete(normalizedId);
      try {
        await deleteTrackingItem(normalizedId);
      } catch (error) {
        setDeletingTrackingIds((prev) => prev.filter((id) => id !== normalizedId));
        setErrorMessage(`Ошибка удаления товара: ${error?.message || 'попробуйте снова.'}`);
      }
    }, LIST_DELETE_ANIMATION_MS);

    deletingTrackingTimersRef.current.set(normalizedId, timer);
  };


  // Если это тот самый user_id, рендерим "особый" вариант
  // if (userInfo.user_id === 7017255909) {
  return (
    <>
      <Container className={`min-vh-100 pb-5 ${themeClass} scrollable waiting-list-shell ${theme === 'dark' ? 'waiting-list-shell--dark' : 'waiting-list-shell--light'}`}>
        <div className={`main-status-glass ${theme === 'dark' ? 'main-status-glass--dark' : 'main-status-glass--light'}`} aria-hidden="true" />
        <div className="waiting-list-topbar">
          <div>
            <div className={`profile-header rounded ${themeClass} shadow waiting-list-topbar__card`} style={headerStyle}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="mb-0">Лист Отслеживания 🛒</h1>

                {/* Новая кнопка добавления */}
                {trackingItems.length < 300 && userInfo?.status && (
                  <button
                    onClick={() => setShowModal(true)}
                    className={`btn btn-${theme === 'dark' ? 'outline-light' : 'outline-primary'
                      } p-2`}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PlusCircle size={24} />
                  </button>
                )}
              </div>

              {/* Улучшенный поиск */}
              <div className="position-relative">
                <Form.Control
                  type="search"
                  placeholder="Поиск по названию или бренду..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`waiting-list-search-input ${theme === 'dark' ? 'text-white' : 'text-dark'}`}
                  style={{
                    paddingLeft: '2.5rem',
                    borderRadius: '20px'
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  className="position-absolute"
                  style={{
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fill: theme === 'dark' ? '#adb5bd' : '#6c757d',
                    pointerEvents: 'none'
                  }}
                  viewBox="0 0 24 24"
                >
                  <path d="M10 18a7.952 7.952 0 0 0 4.897-1.688l4.396 4.396 1.414-1.414-4.396-4.396A7.952 7.952 0 0 0 18 10c0-4.411-3.589-8-8-8s-8 3.589-8 8 3.589 8 8 8zm0-14c3.309 0 6 2.691 6 6s-2.691 6-6 6-6-2.691-6-6 2.691-6 6-6z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="waiting-list-topbar-spacer" aria-hidden="true" />

        {filteredItems.length > 0 && (
          <Row className="mb-3">
            {filteredItems.map(item => {
              const isDeletingItem = deletingTrackingIds.includes(Number(item?.id || 0));
              // Расчёт диапазона цен (если есть данные в diapaz)
              const hasRange = item.diapaz && item.diapaz.length > 0;
              const minPrice = hasRange
                ? Math.round(Math.min(...item.diapaz.map(pair => pair[1])))
                : null;
              const maxPrice = hasRange
                ? Math.round(Math.max(...item.diapaz.map(pair => pair[1])))
                : null;

              return (
                <Col
                  key={item.id}
                  xs={12}
                  className={`mb-3 waiting-list-item-anim ${isDeletingItem ? 'waiting-list-item-anim--deleting' : ''}`}
                  ref={(el) => (itemRefs.current[item.article] = el)}
                >
                  <div
                    className={`card shadow ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                    style={{
                      borderRadius: '0.5rem',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'row',
                      height: '280px', // Фиксированная высота карточки
                      width: '100%' // Занимает всю доступную ширину
                    }}
                  >
                    {/* Левая часть — изображение */}
                    <div style={{ flex: '0 0 150px', position: 'relative' }}>
                      <img
                        src={item.linkphoto[0]}
                        alt={item.name}
                        style={{
                          height: '100%',
                          objectFit: 'cover',
                          maxWidth: 150,
                        }}
                      />
                    </div>

                    {/* Правая часть — контент */}
                    <div
                      className="card-body d-flex flex-column justify-content-between"
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0 // Важно для корректного обрезания контента
                      }}
                    >
                      <div>
                        {/* Название и бренд */}
                        <h6 className="card-title mb-1 waiting-list-item-title" style={{ fontWeight: '600' }}>
                          <a
                            href={`https://www.wildberries.ru/catalog/${item.article}/detail.aspx`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="waiting-list-item-title-link"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {item.name}
                          </a>
                        </h6>

                        <p className="card-subtitle justify-content-between mb-2 waiting-list-item-brand-row" style={{ display: 'flex', fontSize: '0.85rem', color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>
                          <span className="waiting-list-item-brand">{item.brand}</span>
                          <span className="waiting-list-item-article" style={{ marginLeft: '6px' }}><Codee>{item.article}</Codee></span>
                        </p>

                        {/* Блок с текущей и желаемой ценой */}
                        <div className="d-flex justify-content-between mb-2">
                          <div className="text-center">
                            <small style={{ fontSize: '0.75rem', color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>Сейчас</small>
                            <div style={{ fontWeight: '500' }}>
                              {item.price_now.toLocaleString('ru-RU')}₽
                            </div>
                          </div>
                          <div className="text-center">
                            <small style={{ fontSize: '0.75rem', color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>Желаемая</small>
                            <div style={{ fontWeight: '500' }}>
                              {item.price_want.toLocaleString('ru-RU')}₽
                            </div>
                          </div>
                        </div>

                        {/* Диапазон цен */}
                        <div
                          className="mb-2 p-1 rounded text-center"
                          style={{
                            background: theme === 'dark' ? 'rgba(35, 50, 66, 0.7)' : 'rgba(255, 255, 255, 0.66)',
                            cursor: hasRange ? 'pointer' : 'default',
                            fontSize: '0.9rem'
                          }}
                          onClick={() => {
                            if (hasRange) {
                              // const chartData = [
                              //   ...item.diapaz,
                              //   [Math.floor(Date.now() / 1000), item.price_now]
                              // ];
                              setChartItem(item.diapaz);
                              setShowChartModal(true);
                            }
                          }}
                        >
                          {hasRange ? (
                            <>
                              <div>
                                <span style={{ fontSize: '1.2rem' }}>📊</span>
                                {minPrice.toLocaleString('ru-RU')}₽ – {maxPrice.toLocaleString('ru-RU')}₽
                                <small
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 8,
                                    fontSize: '0.75em',
                                    color: theme === 'dark' ? '#a0a0a0' : '#666666',
                                    opacity: 0.9,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  {item.diapaz && item.diapaz.length >= 2 && (
                                    <>
                                      {getPriceChange(item.diapaz).hasChange && (
                                        <span style={{
                                          color: getPriceChange(item.diapaz).isIncrease
                                            ? '#ff4444'
                                            : getPriceChange(item.diapaz).isDecrease
                                              ? '#4CAF50'
                                              : 'inherit',
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}>
                                          {getPriceChange(item.diapaz).isIncrease ? '↑' : '↓'}
                                          {Math.abs(getPriceChange(item.diapaz).diff).toLocaleString('ru-RU')}₽
                                          <span style={{ fontSize: '0.8em', marginLeft: '2px' }}>
                                            ({getPriceChange(item.diapaz).isIncrease ? '+' : ''}{getPriceChange(item.diapaz).percent}%)
                                          </span>
                                        </span>
                                      )}
                                      <span>|</span>
                                    </>
                                  )}
                                  {new Date(item.diapaz[item.diapaz.length - 1][0] * 1000)
                                    .toLocaleDateString('ru-RU', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                </small>
                              </div>
                            </>
                          ) : (
                            'Нет диапазона'
                          )}
                        </div>

                        {/* Блок размеров с горизонтальным скроллом */}
                        {/* Блок размеров с кастомным скроллом */}
                        {item.sizes && Object.keys(item.sizes).length > 0 && (
                          <div
                            style={{
                              overflowX: 'auto',
                              flexShrink: 0,
                              maxHeight: '60px',
                              position: 'relative',
                              marginBottom: '0.5rem',
                              // Скрываем стандартный скролл
                              scrollbarWidth: 'none', /* Firefox */
                              msOverflowStyle: 'none', /* IE/Edge */
                            }}
                            className="scroll-container"
                          >

                            <div
                              className="d-flex flex-nowrap gap-2"
                              style={{
                                paddingBottom: '4px',
                                minWidth: 'min-content',
                              }}
                            >
                              {Object.entries(item.sizes).map(([sizeKey, [wbStock, sellerStock, _, isSelected]]) => {
                                if (isSelected) {
                                  const mainSize = sizeKey.split('(')[0];
                                  return (
                                    <div
                                      key={sizeKey}
                                      className="d-flex align-items-center rounded px-2 py-1"
                                      style={{
                                        fontSize: '0.8rem',
                                        border: '1px solid',
                                        borderColor: theme === 'dark' ? '#555' : '#dee2e6',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                      }}
                                    >
                                      <span className="fw-bold me-1">{mainSize}</span>
                                      <div className="d-flex gap-1">
                                        <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                                          {wbStock}
                                        </span>
                                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem' }}>
                                          {sellerStock}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }

                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Кнопки действий */}
                      <div className="d-flex justify-content-around">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => handleEditItem(item)}
                          disabled={!userInfo?.status}
                        >
                          <PencilSquare size={20} color={theme === 'dark' ? '#fff' : '#000'} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            updateTrackingItemPause(item.id, !item.pause)
                          }}
                          disabled={!userInfo?.status}
                        >
                          {!item.pause ? (
                            <PauseBtn size={20} color="red" />
                          ) : (
                            <PlayBtn size={20} color="green" />
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            handleAnimatedTrackingDelete(item.id);
                          }}
                          disabled={isDeletingItem}
                        >
                          <Trash3 size={20} color={theme === 'dark' ? '#fff' : '#000'} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
        {filteredItems.length === 0 && (
          <div className="text-center py-5">
            <div className="h4">😕 Ничего не найдено</div>
            <p className="mt-2">
              Попробуйте изменить запрос или добавьте новый товар
            </p>
          </div>
        )}

        {/* Проверяем, сколько товаров в списке, и если < 300, показываем кнопку добавления */}

      </Container>

      {/* Модалка для добавления нового товара */}
      <Modal show={showModal} onHide={() => setShowModal(false)} className={theme === 'dark' ? 'text-white modal-dark waiting-list-modal' : 'text-dark modal-light waiting-list-modal'}>
        <Modal.Header className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-black'}>
          <Modal.Title>Добавить новый товар</Modal.Title>
          <CloseButton onClick={() => setShowModal(false)} className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'} />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'text-black bg-light'}>
          <Form>
            <Form.Group controlId="trackingItemInput">
              <Form.Label>Введите артикул</Form.Label>
              <Form.Control
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Только цифры"
                isInvalid={!!errorMessage}
              />
              <Form.Control.Feedback
                style={{ color: errorMessage.includes('дубликат') ? 'orange' : undefined }}
                type="invalid"
              >
                {errorMessage}
              </Form.Control.Feedback>

            </Form.Group>
          </Form>
          <div className="d-flex justify-content-between gap-2 mt-3">
            <Button variant="secondary" className="waiting-list-add-btn waiting-list-add-btn--secondary" onClick={handlePasteFromClipboard}>
              Вставить из буфера
            </Button>
            <Button variant="primary" className="waiting-list-add-btn waiting-list-add-btn--primary" onClick={handleAddItem}>
              Добавить
            </Button>
          </div>
        </Modal.Body>

      </Modal>
      {/* Модалка успешного добавления */}
      <Modal
        show={showSuccessModal}
        onHide={handleCloseSuccessModal}

        className={theme === 'dark' ? 'text-white modal-dark waiting-list-modal waiting-list-config-modal' : 'text-dark modal-light waiting-list-modal waiting-list-config-modal'}
        size="lg"

      >
        <div className="waiting-list-config-shell" style={{ opacity: showChartModal ? 0.5 : 1, transition: 'opacity 0.3s ease' }}>
          <Modal.Header className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-black'}>
            <Modal.Title>Настройка отслеживания</Modal.Title>
            <CloseButton onClick={handleCloseSuccessModal} className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'} />
          </Modal.Header>

          <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'text-black bg-light'}>
            {itemData && (
              <Row className="g-4">
                {/* Левая колонка с фотографиями */}
                <Col md={6}>
                  <Carousel interval={null} controls indicators>
                    {itemData.data.links_photo.map((image, index) => (
                      <Carousel.Item key={index}>
                        <img
                          className="d-block w-100"
                          src={image.replace("c246x328", "c516x688")}
                          alt={`Slide ${index}`}
                          style={{ borderRadius: '8px' }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Col>

                {/* Правая колонка с информацией */}
                <Col md={6}>
                  <div className="d-flex flex-column h-100">
                    {/* Название и бренд */}
                    <h4 className="mb-1">{itemData.data.name}</h4>
                    <div
                      className="mb-3"
                      style={{
                        color: theme === 'dark' ? '#adb5bd' : '#6c757d',
                        fontSize: '0.9rem'
                      }}
                    >
                      {itemData.data.brand}
                    </div>

                    {/* Цены */}
                    <div className="d-flex justify-content-between align-items-end mb-4">
                      <div>
                        <div className="h3 mb-0">
                          {itemData.data.price_now.toLocaleString('ru-RU', {
                            style: 'currency',
                            currency: 'RUB',
                            maximumFractionDigits: 0
                          })}
                        </div>
                        <div
                          className="small text-end mt-1"
                          style={{ color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>Текущая цена</div>
                      </div>

                      <div style={{ width: '110px' }}>
                        <Form.Control
                          type="number"
                          placeholder="50"
                          min="1"
                          step="100"
                          className={`waiting-list-price-input border-0 border-bottom rounded-0 px-0 bg-transparent text-end`}
                          style={{
                            boxShadow: 'none',
                            fontSize: '1.5rem',
                            color: theme === 'dark' ? '#fff' : '#000',
                            fontWeight: '500',
                            padding: '0',
                            marginBottom: '-6px',
                            '--placeholder-color': theme === 'dark' ? '#6c757d' : '#adb5bd',
                            '--placeholder-opacity': 1,

                          }}
                          data-custom-placeholder
                          value={priceWant}
                          onChange={handlePriceWantChange}
                        />
                        <div
                          className="small text-end mt-1"
                          style={{ color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}
                        >
                          Желаемая цена
                        </div>
                      </div>
                    </div>

                    {/* Диапазон цен */}
                    {itemData.data.price_history ? (
                      <div
                        className="d-flex align-items-center justify-content-center gap-2 mb-4 p-2 rounded"
                        style={{
                          background: theme === 'dark' ? 'rgba(35, 50, 66, 0.7)' : 'rgba(255, 255, 255, 0.66)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => {
                          const chartData = [
                            ...itemData.data.price_history,
                            [Math.floor(Date.now() / 1000), itemData.data.price_now]
                          ];
                          setChartItem(chartData);
                          setShowChartModal(true);

                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📊</span>
                        <span className="fw-medium">
                          {Math.round(Math.min(...itemData.data.price_history.map(pair => pair[1]))).toLocaleString('ru-RU')}₽ —{' '}
                          {Math.round(Math.max(...itemData.data.price_history.map(pair => pair[1]))).toLocaleString('ru-RU')}₽
                        </span>
                        <small
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 8,
                            fontSize: '0.75em',
                            color: theme === 'dark' ? '#a0a0a0' : '#666666',
                            opacity: 0.9,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {itemData.data.price_history.length >= 2 && (
                            <>
                              {getPriceChange(itemData.data.price_history).hasChange && (
                                <span style={{
                                  color: getPriceChange(itemData.data.price_history).isIncrease
                                    ? '#ff4444'
                                    : getPriceChange(itemData.data.price_history).isDecrease
                                      ? '#4CAF50'
                                      : 'inherit',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {getPriceChange(itemData.data.price_history).isIncrease ? '↑' : '↓'}
                                  {Math.abs(getPriceChange(itemData.data.price_history).diff).toLocaleString('ru-RU')}₽
                                  <span style={{ fontSize: '0.8em', marginLeft: '2px' }}>
                                    ({getPriceChange(itemData.data.price_history).isIncrease ? '+' : ''}{getPriceChange(itemData.data.price_history).percent}%)
                                  </span>
                                </span>
                              )}
                              <span>|</span>
                            </>
                          )}
                          {new Date(itemData.data.price_history[itemData.data.price_history.length - 1][0] * 1000)
                            .toLocaleDateString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                        </small>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center gap-2 mb-4 p-2 rounded"
                        style={{
                          background: theme === 'dark' ? 'rgba(35, 50, 66, 0.7)' : 'rgba(255, 255, 255, 0.66)',
                          cursor: 'not-allowed',
                          opacity: 0.7
                        }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📊</span>
                        <span className="fw-medium">Нет диапазона</span>
                      </div>
                    )}


                    {/* Размеры 310509107*/}
                    <div className="mb-4 flex-grow-1">
                      <h6 className="mb-3">Выберите размеры для отслеживания:</h6>
                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                        {(() => {
                          const hasDifferentPrice = Object.values(itemData.data.sizes).some(
                            ([, , sizePrice]) => sizePrice !== itemData.data.price_now
                          );

                          return Object.entries(itemData.data.sizes).map(
                            ([size, [wbStock, sellerStock, price, isSelected]]) => {
                              // Обработка длинных названий размеров
                              const [displaySize, subSize] = size.split(/[()]/).filter(Boolean);
                              const isCompact = size.length <= 8;

                              return (
                                <Button
                                  key={size}
                                  variant={isSelected ? 'primary' : 'outline-secondary'}
                                  className="position-relative p-2 d-flex flex-column align-items-center"
                                  style={{
                                    minWidth: '80px',
                                    maxWidth: '200px',
                                    borderWidth: '2px',
                                    borderColor: isSelected
                                      ? '#0d6efd'
                                      : theme === 'dark' ? '#444' : '#dee2e6',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    lineHeight: '1.1',
                                    backgroundColor: theme === 'dark'
                                      ? isSelected
                                        ? '#0d6efd'
                                        : 'rgba(35, 50, 66, 0.7)'
                                      : 'rgba(255, 255, 255, 0.76)',
                                  }}
                                  onClick={() => handleSizeToggle(size)}
                                >
                                  <div className="fw-bold mb-1" style={{
                                    fontSize: subSize ? '0.85rem' : '1rem',
                                    color: theme === 'dark' ? '#fff' : '#2a2a2a'
                                  }}>
                                    {displaySize}
                                    {subSize && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: theme === 'dark' ? '#bbb' : '#666',
                                        marginTop: '2px'
                                      }}>
                                        {subSize}
                                      </div>
                                    )}
                                  </div>

                                  {hasDifferentPrice && (
                                    <div
                                      className="small"
                                      style={{
                                        color: theme === 'dark' ? '#ccc' : '#666',
                                        marginBottom: '4px',
                                        fontWeight: '500'
                                      }}
                                    >
                                      {price.toLocaleString('ru-RU')}₽
                                    </div>
                                  )}

                                  <div className="d-flex gap-1 w-100 justify-content-center">
                                    <div
                                      className="rounded-pill px-2"
                                      style={{
                                        backgroundColor: 'green',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        minWidth: '24px',
                                        lineHeight: '1.4'
                                      }}
                                    >
                                      {wbStock}
                                    </div>
                                    <div
                                      className="rounded-pill px-2"
                                      style={{
                                        backgroundColor: 'orange',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        minWidth: '24px',
                                        lineHeight: '1.4'
                                      }}
                                    >
                                      {sellerStock}
                                    </div>
                                  </div>
                                </Button>
                              );
                            }
                          );
                        })()}
                      </div>
                    </div>

                    {/* Чекбоксы */}
                    <div className="border-top pt-3">
                      <Form>
                        <Form.Group>
                          <Form.Check
                            type="checkbox"
                            label="Уведомлять при любом снижении цены от 10%?"
                            checked={trackPrice}
                            onChange={(e) => void handleTrackPriceChange(e.target.checked)}
                            className="mb-2"
                          />

                          <Form.Check
                            type="checkbox"
                            label="Уведомлять о появлении в наличии?"
                            checked={trackAvailability}
                            onChange={(e) => void handleTrackAvailabilityChange(e.target.checked)}
                            className="mb-2"
                          />

                          <Form.Check
                            type="checkbox"
                            label="Учитывать склад продавца?"
                            checked={trackSeller}
                            onChange={(e) => setTrackSeller(e.target.checked)}
                            className="mb-2"
                          />
                        </Form.Group>
                      </Form>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Modal.Body>

          <Modal.Footer className={theme === 'dark' ? 'border-top border-secondary bg-dark text-white' : 'border-top border-secondary bg-light text-black'}>
            <Button
              variant="secondary"
              className="waiting-list-modal-btn waiting-list-modal-btn--secondary"
              onClick={handleCloseSuccessModal}
              style={{ minWidth: '100px' }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              className="waiting-list-modal-btn waiting-list-modal-btn--primary"
              onClick={handleSave}
              style={{ minWidth: '100px' }}
              disabled={!priceWant || !hasAtLeastOneSelectedSize(itemData.data.sizes)}
            >
              Сохранить
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        className={theme === 'dark' ? 'text-white modal-dark waiting-list-modal' : 'text-dark modal-light waiting-list-modal'}
        size="lg"
        centered
      >
        <Modal.Header className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-black'}>
          <Modal.Title>Редактирование отслеживания</Modal.Title>
          <CloseButton onClick={() => setShowEditModal(false)} className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'} />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          {editItemData && (
            <Row className="g-4">
              {/* Левая колонка – фото */}
              <Col md={6}>
                <Carousel interval={null} controls indicators>
                  {editItemData.linkphoto.map((image, index) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100"
                        src={image.replace("c246x328", "c516x688")}
                        alt={`Slide ${index}`}
                        style={{ borderRadius: '8px' }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </Col>

              {/* Правая колонка – информация и настройки */}
              <Col md={6}>
                <div className="d-flex flex-column h-100">
                  <h4 className="mb-1">{editItemData.name}</h4>
                  <div className="mb-3" style={{ color: theme === 'dark' ? '#adb5bd' : '#6c757d', fontSize: '0.9rem' }}>
                    {editItemData.brand}
                  </div>
                  <div className="d-flex justify-content-between align-items-end mb-4">
                    <div>
                      <div className="h3 mb-0">
                        {editItemData.price_now.toLocaleString('ru-RU', {
                          style: 'currency',
                          currency: 'RUB',
                          maximumFractionDigits: 0
                        })}
                      </div>
                      <div className="small text-end mt-1" style={{ color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        Текущая цена
                      </div>
                    </div>
                    <div style={{ width: '110px' }}>
                      <Form.Control
                        type="number"
                        placeholder="50"
                        min="1"
                        step="100"
                        className={`waiting-list-price-input border-0 border-bottom rounded-0 px-0 bg-transparent text-end`}
                        style={{
                          boxShadow: 'none',
                          fontSize: '1.5rem',
                          color: theme === 'dark' ? '#fff' : '#000',
                          fontWeight: '500',
                          padding: '0',
                          marginBottom: '-6px'
                        }}
                        value={editPriceWant}
                        onChange={(e) => setEditPriceWant(e.target.value)}
                      />
                      <div className="small text-end mt-1" style={{ color: theme === 'dark' ? '#adb5bd' : '#6c757d' }}>
                        Желаемая цена
                      </div>
                    </div>
                  </div>

                  {/* Блок с диапазоном (если есть price_history) */}
                  {editItemData.diapaz ? (
                    <div
                      className="d-flex align-items-center justify-content-center gap-2 mb-4 p-2 rounded"
                      style={{
                        background: theme === 'dark' ? 'rgba(35, 50, 66, 0.7)' : 'rgba(255, 255, 255, 0.66)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => {
                        // const chartData = [
                        //   ...editItemData.diapaz,
                        //   [Math.floor(Date.now() / 1000), editItemData.price_now]
                        // ];
                        setChartItem(editItemData.diapaz);
                        setShowChartModal(true);
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    >
                      <span style={{ fontSize: '1.2rem' }}>📊</span>
                      <span className="fw-medium">
                        {Math.round(Math.min(...editItemData.diapaz.map(pair => pair[1]))).toLocaleString('ru-RU')}₽ —{' '}
                        {Math.round(Math.max(...editItemData.diapaz.map(pair => pair[1]))).toLocaleString('ru-RU')}₽
                      </span>
                      <small
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 8,
                          fontSize: '0.75em',
                          color: theme === 'dark' ? '#a0a0a0' : '#666666',
                          opacity: 0.9,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {editItemData.diapaz && editItemData.diapaz.length >= 2 && (
                          <>
                            {getPriceChange(editItemData.diapaz).hasChange && (
                              <span style={{
                                color: getPriceChange(editItemData.diapaz).isIncrease
                                  ? '#ff4444'
                                  : getPriceChange(editItemData.diapaz).isDecrease
                                    ? '#4CAF50'
                                    : 'inherit',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                {getPriceChange(editItemData.diapaz).isIncrease ? '↑' : '↓'}
                                {Math.abs(getPriceChange(editItemData.diapaz).diff).toLocaleString('ru-RU')}₽
                                <span style={{ fontSize: '0.8em', marginLeft: '2px' }}>
                                  ({getPriceChange(editItemData.diapaz).isIncrease ? '+' : ''}{getPriceChange(editItemData.diapaz).percent}%)
                                </span>
                              </span>
                            )}
                            <span>|</span>
                          </>
                        )}
                        {new Date(editItemData.diapaz[editItemData.diapaz.length - 1][0] * 1000)
                          .toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                      </small>

                    </div>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center gap-2 mb-4 p-2 rounded"
                      style={{
                        background: theme === 'dark' ? 'rgba(35, 50, 66, 0.7)' : 'rgba(255, 255, 255, 0.66)',
                        cursor: 'not-allowed',
                        opacity: 0.7
                      }}
                    >
                      <span style={{ fontSize: '1.2rem' }}>📊</span>
                      <span className="fw-medium">Нет диапазона</span>
                    </div>
                  )}

                  {/* Блок выбора размеров */}
                  <div className="mb-4 flex-grow-1">
                    <h6 className="mb-3">Выберите размеры для отслеживания:</h6>
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {(() => {
                        const hasDifferentPrice = Object.values(editItemData.sizes).some(
                          ([, , sizePrice]) => sizePrice !== editItemData.price_now
                        );


                        return Object.entries(editItemData.sizes).map(([size, [wbStock, sellerStock, price, isSelected]]) => {

                          const [displaySize, subSize] = size.split(/[()]/).filter(Boolean);
                          return (
                            <Button
                              key={size}
                              variant={isSelected ? 'primary' : 'outline-secondary'}
                              className="position-relative p-2 d-flex flex-column align-items-center"
                              style={{
                                minWidth: '80px',
                                maxWidth: '200px',
                                borderWidth: '2px',
                                borderColor: isSelected ? '#0d6efd' : theme === 'dark' ? '#444' : '#dee2e6',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: '1.1',
                                backgroundColor: theme === 'dark' ? (isSelected ? '#0d6efd' : 'rgba(35, 50, 66, 0.7)') : 'rgba(255, 255, 255, 0.76)',
                              }}
                              onClick={() => handleEditSizeToggle(size)}
                            >
                              <div className="fw-bold mb-1" style={{
                                fontSize: subSize ? '0.85rem' : '1rem',
                                color: theme === 'dark' ? '#fff' : '#2a2a2a'
                              }}>
                                {displaySize}
                                {subSize && (
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: theme === 'dark' ? '#bbb' : '#666',
                                    marginTop: '2px'
                                  }}>
                                    {subSize}
                                  </div>
                                )}
                              </div>

                              {hasDifferentPrice && (
                                <div
                                  className="small"
                                  style={{
                                    color: theme === 'dark' ? '#ccc' : '#666',
                                    marginBottom: '4px',
                                    fontWeight: '500'
                                  }}
                                >
                                  {price.toLocaleString('ru-RU')}₽
                                </div>
                              )}
                              <div className="d-flex gap-1 w-100 justify-content-center">
                                <div className="rounded-pill px-2" style={{
                                  backgroundColor: 'green',
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  minWidth: '24px',
                                  lineHeight: '1.4'
                                }}>
                                  {wbStock}
                                </div>
                                <div className="rounded-pill px-2" style={{
                                  backgroundColor: 'orange',
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  minWidth: '24px',
                                  lineHeight: '1.4'
                                }}>
                                  {sellerStock}
                                </div>
                              </div>
                            </Button>
                          );
                        })
                      })()}
                    </div>
                  </div>

                  {/* Чекбоксы уведомлений */}
                  <div className="border-top pt-3">
                    <Form>
                      <Form.Group>
                        <Form.Check
                          type="checkbox"
                          label="Уведомлять при любом снижении цены от 10%?"
                          checked={editTrackPrice}
                          onChange={(e) => void handleEditTrackPriceChange(e.target.checked)}
                          className="mb-2"
                        />
                        <Form.Check
                          type="checkbox"
                          label="Уведомлять о появлении в наличии?"
                          checked={editTrackAvailability}
                          onChange={(e) => void handleEditTrackAvailabilityChange(e.target.checked)}
                          className="mb-2"
                        />
                        <Form.Check
                          type="checkbox"
                          label="Учитывать склад продавца?"
                          checked={editTrackSeller}
                          onChange={(e) => setEditTrackSeller(e.target.checked)}
                          className="mb-2"
                        />
                      </Form.Group>
                    </Form>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className={theme === 'dark' ? 'border-top border-secondary bg-dark text-white' : 'border-top border-secondary bg-light text-black'}>
          <Button variant="secondary" className="waiting-list-modal-btn waiting-list-modal-btn--secondary" onClick={() => setShowEditModal(false)} style={{ minWidth: '100px' }}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="waiting-list-modal-btn waiting-list-modal-btn--primary"
            onClick={handleEditSave}
            style={{ minWidth: '100px' }}
            disabled={!editPriceWant || !hasAtLeastOneSelectedSize(editItemData.sizes)}
          >
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showChartModal}
        onHide={() => {
          setChartItem(null);
          setShowChartModal(false);
        }}
        size="lg"
        centered
        className={theme === 'dark' ? 'text-white modal-dark waiting-list-modal' : 'text-dark modal-light waiting-list-modal'}
      >
        <Modal.Header className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-black'}>
          <Modal.Title>📈 Анализ цен</Modal.Title>
          <CloseButton onClick={() => setShowChartModal(false)} className={theme === 'dark' ? 'btn-close-white' : 'btn-close-black'} />
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          {chartItem && chartItem.length > 0 ? (
            <>
              <div className="d-flex justify-content-center mb-4 gap-2">
                <Button
                  variant={selectedPeriod === '1m' ? 'primary' : theme === 'dark' ? 'outline-light' : 'outline-dark'}
                  onClick={() => setSelectedPeriod('1m')}
                  size="sm"
                >
                  1 мес
                </Button>
                <Button
                  variant={selectedPeriod === '3m' ? 'primary' : theme === 'dark' ? 'outline-light' : 'outline-dark'}
                  onClick={() => setSelectedPeriod('3m')}
                  size="sm"
                >
                  3 мес
                </Button>
                <Button
                  variant={selectedPeriod === '6m' ? 'primary' : theme === 'dark' ? 'outline-light' : 'outline-dark'}
                  onClick={() => setSelectedPeriod('6m')}
                  size="sm"
                >
                  6 мес
                </Button>
                <Button
                  variant={selectedPeriod === 'all' ? 'primary' : theme === 'dark' ? 'outline-light' : 'outline-dark'}
                  onClick={() => setSelectedPeriod('all')}
                  size="sm"
                >
                  Все время
                </Button>
              </div>


              <div className="d-flex justify-content-center gap-3 mb-4">
                {/* Минимальная цена */}
                {chartItem && chartItem.length > 0 && (
                  <>
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span style={{ fontSize: '1.2rem', color: theme === 'dark' ? '#4ade80' : '#16a34a' }}>📉</span>
                        <div>
                          <div className={`small ${theme === 'dark' ? 'text-white-50' : 'text-muted'}`}>Минимум</div>
                          <div className="h5 mb-0 fw-bold" style={{ color: theme === 'dark' ? '#60a5fa' : '#2563eb' }}>
                            {Math.min(...chartItem.map(p => p[1]))?.toLocaleString('ru-RU')}₽
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Разделитель */}
                    <div style={{ width: '1px', backgroundColor: theme === 'dark' ? '#444' : '#ddd' }}></div>

                    {/* Умная цена */}
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span style={{ fontSize: '1.2rem', color: theme === 'dark' ? '#60a5fa' : '#2563eb' }}>⚡️</span>
                        <div>
                          <div className={`small ${theme === 'dark' ? 'text-white-50' : 'text-muted'}`}>Умная цена</div>
                          <div className="h5 mb-0 fw-bold" style={{ color: theme === 'dark' ? '#4ade80' : '#16a34a' }}>
                            {calculateWeightedMedian(processPrices(chartItem))?.toLocaleString('ru-RU') || 'Н/Д'}₽
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Разделитель */}
                    <div style={{ width: '1px', backgroundColor: theme === 'dark' ? '#444' : '#ddd' }}></div>

                    {/* Максимальная цена */}
                    <div className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span style={{ fontSize: '1.2rem', color: theme === 'dark' ? '#f87171' : '#dc2626' }}>📈</span>
                        <div>
                          <div className={`small ${theme === 'dark' ? 'text-white-50' : 'text-muted'}`}>Максимум</div>
                          <div className="h5 mb-0 fw-bold" style={{ color: theme === 'dark' ? '#f87171' : '#dc2626' }}>
                            {Math.max(...chartItem.map(p => p[1]))?.toLocaleString('ru-RU')}₽
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>


              <PriceChart
                data={chartItem.filter(item => {
                  const now = new Date();
                  const itemDate = new Date(item[0] * 1000);
                  switch (selectedPeriod) {
                    case '1m': return itemDate > new Date(now.setMonth(now.getMonth() - 1));
                    case '3m': return itemDate > new Date(now.setMonth(now.getMonth() - 3));
                    case '6m': return itemDate > new Date(now.setMonth(now.getMonth() - 6));
                    default: return true;
                  }
                })}
                theme={theme}
              />


              <div className={`text-center mt-3 small ${theme === 'dark' ? 'text-white-50' : 'text-muted'}`}>

                * Умная базовая цена рассчитывается с учетом продолжительности периодов и исключаются резкие повышения
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="h4">😕 Нет данных</div>
              <p className="mt-2">История изменения цен недоступна</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <BottomNav />
    </>
  );
  // }


};

export default WaitingListPage;
